/**
 * Research v2 (docs/research-design.md): three areas (Fizika, Társadalom, Mérnöki), each with its own research places,
 * queue, RP and card hand; a fixed spine plus cards ("Lehetőségek"); labs that belong to an area; the whole tree by tier.
 * The server decides availability, price, timing and the hand; this view only shows it and sends commands.
 * Touch targets are >= 44 px; on narrow screens the areas are tabs, on wide screens three columns.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { renderStored, type ResearchState, type TechArea } from '@galaxis/shared';
import { fetchResearch, startResearch, cancelResearch, buildLab, upgradeLab, fetchResources } from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';
import { syncResources } from '../economy/resource-sync.ts';
import { previewResourceSpend, useLiveResources } from '../economy/live-store.ts';
import { StorageGauge } from './StorageGauge.tsx';
import { AreaPanel } from './research/AreaPanel.tsx';
import { LabsPanel } from './research/LabsPanel.tsx';
import { ScientistsPanel } from './research/ScientistsPanel.tsx';
import { TechDetail } from './research/TechDetail.tsx';
import { AREAS, AREA_META, EXPENSIVE_METAL, num, type TechClient } from './research/research-format.ts';

export function ResearchView() {
  const [state, setState] = useState<ResearchState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [tab, setTab] = useState<TechArea>('physics');
  const [detail, setDetail] = useState<{ id: string; confirming: boolean } | null>(null);
  const version = useGameStore(s => s.researchVersion);
  const { pushToast, setResources } = useGameStore();
  const live = useLiveResources();
  const prev = useRef<ResearchState | null>(null);

  const load = useCallback(() => {
    fetchResearch().then(s => {
      const before = prev.current;
      prev.current = s;
      setState(s); setError(null);
      if (before) announceChanges(before, s, pushToast);
    }).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
  }, [pushToast]);
  useEffect(() => { load(); }, [load, version]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  // Safety net if a completion push is missed
  const nextDue = state ? Math.min(Infinity, ...state.active.map(a => new Date(a.completesAt).getTime()), ...state.labs.flatMap(l => l.completesAt ? [new Date(l.completesAt).getTime()] : [])) : Infinity;
  const due = nextDue <= now;
  useEffect(() => { if (due) { const t = setTimeout(load, 1500); return () => clearTimeout(t); } }, [due, load]);

  const run = async (fn: () => Promise<string | void>) => {
    setBusy(true);
    try {
      const msg = await fn();
      if (msg) pushToast(msg);
      setResources(await fetchResources());
      load();
    } catch (e) {
      pushToast(e instanceof Error ? e.message : 'A művelet nem sikerült', 'error');
      void syncResources(); // a previewed cost that was not charged goes back to the server's number
      load();
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="flex-1 p-6 text-red-400 text-sm">{error}</div>;
  if (!state) return <div className="flex-1 p-6 text-slate-500 text-sm">Kutatás betöltése…</div>;

  const areaOf = (a: TechArea) => state.areas.find(x => x.area === a)!;

  const doStart = (t: TechClient) => {
    const area = areaOf(t.area);
    const c = t.effectiveCost;
    const willQueue = area.slots.used >= area.slots.total || area.queue.length > 0;
    setDetail(null);
    void run(async () => {
      // Instant feedback: the bar shows the price at once; a refusal puts the server's number back
      if (!willQueue) previewResourceSpend(c.metal, c.energy, c.rp);
      const r = await startResearch(t.id);
      return r.status === 'queued' ? `Sorba állítva: ${t.name}` : `Kutatás elindítva: ${t.name}`;
    });
  };
  /** Cheap research starts at once; an expensive one asks first (in the detail sheet). */
  const requestStart = (t: TechClient) => {
    const willQueue = areaOf(t.area).slots.used >= areaOf(t.area).slots.total || areaOf(t.area).queue.length > 0;
    if (!willQueue && t.effectiveCost.metal >= EXPENSIVE_METAL) setDetail({ id: t.id, confirming: true });
    else doStart(t);
  };
  const onCancel = (techId: string, queued: boolean) => void run(async () => {
    const r = await cancelResearch(techId);
    return queued ? 'Törölve a sorból' : `Kutatás megszakítva (vissza: ${r.refunded.metal} fém, ${r.refunded.energy} energia, ${r.refunded.rp} RP)`;
  });
  const detailTech = detail ? state.techs.find(t => t.id === detail.id) : undefined;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="research-view">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-lg font-semibold text-slate-200">Kutatás</h1>
          <p className="text-xs text-slate-400" data-testid="rp-summary">
            Összes RP: <span className="font-mono text-slate-200" data-testid="rp-balance">{Math.floor(state.rp.balance)}</span>
            {' '}· <span className="font-mono text-accent" data-testid="rp-income">+{num(state.rp.perHour)} RP/ó</span>
            {' '}· helyek <span className="font-mono" data-testid="slots">{state.slots.used} / {state.slots.total}</span>
            {' '}· labor-fenntartás <span className="font-mono" data-testid="lab-upkeep">−{state.rp.energyUpkeepPerHour} energia/ó</span>
          </p>
        </div>

        <StorageGauge testId="research-storage" />

        {/* Area tabs (narrow screens) */}
        <div role="tablist" aria-label="Kutatási területek" className="flex gap-2 lg:hidden" data-testid="area-tabs">
          {AREAS.map(a => {
            const st = areaOf(a);
            return (
              <button key={a} type="button" role="tab" id={`tab-${a}`} aria-selected={tab === a} aria-controls={`panel-${a}`} tabIndex={tab === a ? 0 : -1}
                className={`flex-1 min-h-11 rounded-lg border px-2 text-xs ${tab === a ? `${AREA_META[a].ring} bg-slate-800 ${AREA_META[a].tone}` : 'border-slate-700 text-slate-400'}`}
                data-testid={`tab-${a}`}
                onKeyDown={e => {
                  const i = AREAS.indexOf(a);
                  if (e.key === 'ArrowRight') { setTab(AREAS[(i + 1) % 3]!); e.preventDefault(); }
                  if (e.key === 'ArrowLeft') { setTab(AREAS[(i + 2) % 3]!); e.preventDefault(); }
                }}
                onClick={() => setTab(a)}>
                <span aria-hidden>{AREA_META[a].icon}</span> {AREA_META[a].label}
                <span className="block text-[10px] text-slate-500">{st.slots.used}/{st.slots.total} hely · {Math.floor(st.rp.balance)} RP</span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start" data-testid="tech-grid">
          {AREAS.map(a => (
            <div key={a} id={`panel-${a}`} role="tabpanel" aria-labelledby={`tab-${a}`} className={tab === a ? 'block' : 'hidden lg:block'}>
              <AreaPanel area={areaOf(a)} state={state} live={live} now={now} busy={busy} onStart={requestStart} onOpen={t => setDetail({ id: t.id, confirming: false })} onCancel={onCancel} />
            </div>
          ))}
        </div>

        <LabsPanel state={state} now={now} busy={busy} live={live}
          onUpgrade={(labId, tier) => void run(async () => { await upgradeLab(labId); return `Laboratórium fejlesztése: ${tier}. szint`; })}
          onBuild={(systemId, name, area) => void run(async () => { await buildLab(systemId, area); return `${AREA_META[area].label}-laboratórium építése: ${name}`; })} />

        <ScientistsPanel version={version} onChanged={load} />

        {/* Discoveries + log */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Felfedezések" data-testid="discoveries">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Felfedezések ({state.discoveries.length})</h2>
            {state.discoveries.length === 0 && <p className="text-xs text-slate-500">Felmért rendszerekben leletekre bukkanhatsz: adattár (RP), roncsmező (kedvezmény), prekurzor-műtárgy (egyedi technológia, arany keretes kártya).</p>}
            <ul className="space-y-2">
              {state.discoveries.map(d => (
                <li key={d.id} className="text-xs"><b className="text-slate-200">{d.name}</b> — {d.systemName}<br /><span className="text-accent">{d.reward}</span>{d.used && <span className="text-slate-500"> · felhasználva</span>}</li>
              ))}
            </ul>
            {state.techs.filter(t => t.discoveryOnly && t.status === 'done').length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="permanent-cards">
                {state.techs.filter(t => t.discoveryOnly && t.status === 'done').map(t => (
                  <li key={t.id} className="rounded border border-amber-400/80 p-2 text-xs shadow-[0_0_12px_rgba(251,191,36,0.25)]">
                    <b className="text-amber-300">★ {t.name}</b> · állandó kártya<br /><span className="text-emerald-300/90">{t.unlocks.join(' · ')}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="sci-fi-panel rounded-xl p-4" aria-label="Kutatási napló" data-testid="research-log">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Kutatási napló</h2>
            {state.log.length === 0 && <p className="text-xs text-slate-500">Még nincs bejegyzés.</p>}
            <ul className="space-y-1 max-h-56 overflow-y-auto">
              {state.log.map((l, i) => (
                <li key={`${l.at}-${i}`} className="text-xs text-slate-400"><span className="text-slate-600">{new Date(l.at).toLocaleString('hu-HU')}</span> · {renderStored(l.text)}</li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {detail && detailTech && (
        <TechDetail tech={detailTech} area={areaOf(detailTech.area)} state={state} live={live} busy={busy} confirming={detail.confirming}
          onStart={doStart} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}

/** Toasts for what changed since the last picture: a finished research and a fresh hand. */
function announceChanges(before: ResearchState, after: ResearchState, toast: (m: string, kind?: 'info' | 'error') => void): void {
  const was = new Map(before.techs.map(t => [t.id, t.status]));
  for (const t of after.techs) {
    if (t.status === 'done' && was.get(t.id) !== undefined && was.get(t.id) !== 'done') toast(`Kutatás kész: ${t.name}`);
  }
  const seen = new Set(before.log.map(l => `${l.at}|${l.text}`));
  for (const l of after.log) if ((l.kind === 'leader' || l.kind === 'survey') && !seen.has(`${l.at}|${l.text}`)) toast(renderStored(l.text));
  for (const a of after.areas) {
    const b = before.areas.find(x => x.area === a.area);
    if (b && a.hand.drawSeq > b.hand.drawSeq && a.hand.cards.length > 0) toast(`Új lehetőségek: ${AREA_META[a.area].label} (${a.hand.cards.length} kártya)`);
  }
}
